import { toInternationalNumber } from './strings.tools.js'

export interface LatLng {
  lat: number
  lng: number
}

// Bornes terrestres d'un point GPS (validées aussi par `@gozoki/api-types/src/validation/geo`).
export const LATITUDE_MAX = 90
export const LONGITUDE_MAX = 180

/** Point GPS fini et dans les bornes terrestres. */
export const isOnEarth = (lat: number, lng: number) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= LATITUDE_MAX &&
  Math.abs(lng) <= LONGITUDE_MAX

/** Segment `@<lat>,<lng>` du chemin d'une URL Google Maps (`…/place/…/@44.84,-0.57,17z/…`). */
const GOOGLE_MAPS_AT_COORDINATES = /@(-?\d+\.\d+),(-?\d+\.\d+)/

// `try { new URL() }` plutôt que `URL.canParse` : ce paquet tourne aussi dans le navigateur et
// l'app mobile, où `URL.canParse` n'est pas garanti.
const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

/** Paramètre `query=<lat>,<lng>` d'un lien de recherche (format de `googleMapsSearchUrlOf`). */
const GOOGLE_MAPS_QUERY_COORDINATES = /^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/

/**
 * Lien Google Maps de recherche d'un point, celui que le foncier enregistre quand il le calcule
 * (coordonnées saisies ou adresse géocodée) : `…/maps/search/?api=1&query=<lat>%2C<lng>`.
 */
export const googleMapsSearchUrlOf = ({ lat, lng }: LatLng): string =>
  `https://www.google.com/maps/search/?api=1&query=${lat}%2C${lng}`

/**
 * Coordonnées lues dans une URL Google Maps : segment `@<lat>,<lng>` de son chemin (lien copié
 * depuis Google Maps), sinon paramètre `query=<lat>,<lng>` d'un lien de recherche (lien calculé
 * par le foncier, cf. `googleMapsSearchUrlOf`). `null` si l'URL est absente, invalide, sans l'un
 * de ces deux formats ou hors des bornes terrestres.
 */
export const extractLatLngFromGoogleMapsUrl = (
  googleMapsUrl: string | null | undefined,
): LatLng | null => {
  const url = googleMapsUrl ? parseUrl(googleMapsUrl) : null
  if (url === null) return null
  const match =
    GOOGLE_MAPS_AT_COORDINATES.exec(url.pathname) ??
    GOOGLE_MAPS_QUERY_COORDINATES.exec(url.searchParams.get('query')?.trim() ?? '')
  if (match === null) return null
  const lat = Number(match[1])
  const lng = Number(match[2])
  return isOnEarth(lat, lng) ? { lat, lng } : null
}

// Décimal simple, point ou virgule (saisie française) : `Number()` accepterait aussi `0x10` ou
// `1e2`, et `parseFloat` tronquerait `12abc` en 12.
const COORDINATE_PATTERN = /^[+-]?\d+(?:[.,]\d+)?$/

/**
 * Coordonnée saisie ou lue en base, en nombre : décimal simple, virgule décimale acceptée
 * (« 44,84 »). `null` si absente, vide ou illisible (`0x10`, `1e2`, `12abc`). Une chaîne vide
 * n'est pas 0 (`Number('')`), qui placerait le point au large du golfe de Guinée.
 */
export const parseCoordinate = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return COORDINATE_PATTERN.test(trimmed) ? Number(toInternationalNumber(trimmed)) : null
}

/**
 * Paire latitude / longitude saisie : en nombres, ou en chaînes, celles d'un formulaire en cours
 * de saisie, relues par `parseCoordinate`. `null` si l'une manque, est illisible ou si le point
 * n'est pas sur Terre.
 */
export const latLngOf = (latitude: unknown, longitude: unknown): LatLng | null => {
  const lat = parseCoordinate(latitude)
  const lng = parseCoordinate(longitude)
  return lat !== null && lng !== null && isOnEarth(lat, lng) ? { lat, lng } : null
}

/**
 * Point d'un projet du foncier : ses coordonnées saisies (`latLngOf`), sinon celles de son lien
 * Google Maps. Règle partagée par les cartes du foncier (zones d'intérêt, carte des projets, vue
 * aérienne de la fiche) et celles du simulateur logistique (boutique sans point de livraison).
 * `null` si rien ne le situe.
 */
export const storeProjectLatLngOf = (project: {
  latitude?: number | string | null
  longitude?: number | string | null
  googleMapsUrl?: string | null
}): LatLng | null =>
  latLngOf(project.latitude, project.longitude) ??
  extractLatLngFromGoogleMapsUrl(project.googleMapsUrl)
